// src/pages/AddEditStudentPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { students as studentsApi, settings } from '../utils/api';

const COUNTRIES = [
  'Tanzania','Kenya','Uganda','Rwanda','Burundi','Ethiopia','Somalia',
  'South Africa','Nigeria','Ghana','Egypt','Sudan','Zambia','Zimbabwe',
  'Mozambique','Malawi','DRC','Other'
];

export default function AddEditStudentPage() {
  const { id } = useParams();
  const isEdit  = !!id;
  const navigate = useNavigate();
  const qc      = useQueryClient();

  const [form, setForm] = useState({
    registration_no: '', full_name: '', gender: 'male', email: '',
    phone: '', nationality: '', country_code: '', course_id: '', institution_id: '',
    academic_year_id: '', enrollment_year: '', status: 'active', notes: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: courses }      = useQuery({ queryKey: ['courses'],      queryFn: () => settings.courses().then(r => r.data.data) });
  const { data: institutions } = useQuery({ queryKey: ['institutions'], queryFn: () => settings.institutions().then(r => r.data.data) });
  const { data: years }        = useQuery({ queryKey: ['years'],        queryFn: () => settings.academicYears().then(r => r.data.data) });

  const { data: existing } = useQuery({
    queryKey: ['student', id],
    queryFn:  () => studentsApi.get(id).then(r => r.data.data),
    enabled: isEdit
  });

  useEffect(() => {
    if (existing) {
      setForm({
        registration_no:  existing.registration_no  || '',
        full_name:        existing.full_name         || '',
        gender:           existing.gender            || 'prefer_not_to_say',
        email:            existing.email             || '',
        phone:            existing.phone             || '',
        nationality:      existing.nationality       || '',
        country_code:     existing.country_code      || '',
        course_id:        existing.course_id         || '',
        institution_id:   existing.institution_id    || '',
        academic_year_id: existing.academic_year_id  || '',
        enrollment_year:  existing.enrollment_year   || '',
        status:           existing.status            || 'active',
        notes:            existing.notes             || ''
      });
    }
  }, [existing]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.registration_no.trim()) e.registration_no = 'Required';
    if (!form.full_name.trim())       e.full_name       = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert empty strings to null
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

      if (isEdit) {
        await studentsApi.update(id, payload);
        toast.success('Student updated');
      } else {
        const res = await studentsApi.create(payload);
        toast.success('Student registered');
        qc.invalidateQueries(['students']);
        navigate(`/students/${res.data.data.id}`);
        return;
      }
      qc.invalidateQueries(['students']);
      qc.invalidateQueries(['student', id]);
      navigate(`/students/${id}`);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="page-header">
        <div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: 4 }}>
            <Link to="/students">Students</Link> → {isEdit ? 'Edit' : 'New Student'}
          </div>
          <h1 className="page-title">{isEdit ? 'Edit Student' : 'Add New Student'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Personal Information</span></div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Registration Number *</label>
              <input className="form-control" value={form.registration_no}
                onChange={e => set('registration_no', e.target.value)} placeholder="e.g. STD/2024/MED/0001"
                disabled={isEdit} />
              {errors.registration_no && <div className="form-error">{errors.registration_no}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.full_name}
                onChange={e => set('full_name', e.target.value)} placeholder="Full legal name" />
              {errors.full_name && <div className="form-error">{errors.full_name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-control" value={form.gender} onChange={e => set('gender', e.target.value)}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="student@example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-control" value={form.phone}
                onChange={e => set('phone', e.target.value)} placeholder="+255 7XX XXX XXX" />
            </div>
            <div className="form-group">
              <label className="form-label">Nationality</label>
              <select className="form-control" value={form.nationality} onChange={e => set('nationality', e.target.value)}>
                <option value="">Select country</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Academic Information</span></div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Course / Programme</label>
              <select className="form-control" value={form.course_id} onChange={e => set('course_id', e.target.value)}>
                <option value="">Select course</option>
                {courses?.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">School</label>
              <select className="form-control" value={form.institution_id} onChange={e => set('institution_id', e.target.value)}>
                <option value="">Select school</option>
                {institutions?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Academic Year</label>
              <select className="form-control" value={form.academic_year_id} onChange={e => set('academic_year_id', e.target.value)}>
                <option value="">Select year</option>
                {years?.map(y => <option key={y.id} value={y.id}>Year {y.year_number} — {y.label.replace(/ - Y\d+$/, '')}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Enrollment Year</label>
              <input className="form-control" type="number" placeholder="e.g. 2024" min="1900" max="2155"
                value={form.enrollment_year}
                onChange={e => set('enrollment_year', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="graduated">Graduated</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={3} value={form.notes}
              onChange={e => set('notes', e.target.value)} placeholder="Internal notes…" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : (isEdit ? '✓ Save Changes' : '+ Register Student')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
